import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Stack,
  LinearProgress,
  Chip,
  Divider,
  Button,
  CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle as CheckIcon,
  Flag as ObjectiveIcon,
  Warning as RiskIcon,
  Timeline as TimelineIcon,
  Visibility as VisionIcon,
  Analytics as AnalyticsIcon,
  Assignment as ComponentIcon,
  People as ResourceIcon,
  Gavel as GovernanceIcon,
  Dashboard as PerformanceIcon,
  AutoAwesome as AdaptationIcon,
  ArrowForward as ArrowIcon
} from '@mui/icons-material';

import useRoadmapStore from '../store/roadmapStore';
import { STEP_LABELS } from '../utils/constants';
import PageContainer from '../components/PageContainer';
import AICritiqueModal from '../components/AICritiqueModal';
import apiService from '../utils/api';

const STEP_ICONS = {
  vision: <VisionIcon sx={{ fontSize: 16 }} />,
  situation_analysis: <AnalyticsIcon sx={{ fontSize: 16 }} />,
  strategic_objectives: <ObjectiveIcon sx={{ fontSize: 16 }} />,
  action_components: <ComponentIcon sx={{ fontSize: 16 }} />,
  resources: <ResourceIcon sx={{ fontSize: 16 }} />,
  timeline: <TimelineIcon sx={{ fontSize: 16 }} />,
  risks: <RiskIcon sx={{ fontSize: 16 }} />,
  governance: <GovernanceIcon sx={{ fontSize: 16 }} />,
  performance: <PerformanceIcon sx={{ fontSize: 16 }} />,
  adaptation: <AdaptationIcon sx={{ fontSize: 16 }} />
};

const STEP_ROUTES = {
  vision: '/vision', situation_analysis: '/situation', strategic_objectives: '/objectives',
  action_components: '/components', resources: '/resources', timeline: '/timeline',
  risks: '/risks', governance: '/governance', performance: '/performance', adaptation: '/adaptation'
};

export default function DashboardPage() {
  const { roadmapData, getProgress, isStepCompleted, currentRoadmapId } = useRoadmapStore();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [critiqueOpen, setCritiqueOpen] = useState(false);
  const [critiqueData, setCritiqueData] = useState(null);
  const [critiqueLoading, setCritiqueLoading] = useState(false);
  const [narrativeLoading, setNarrativeLoading] = useState(false);

  const handleCritique = async () => {
    if (!currentRoadmapId) return;
    setCritiqueOpen(true);
    setCritiqueLoading(true);
    try {
      const result = await apiService.critiqueRoadmap(currentRoadmapId, i18n.language);
      setCritiqueData(result);
    } catch {
      setCritiqueData({ overall_score: 0, summary: t('ai.generationFailed'), strengths: [], critical_gaps: [], quick_wins: [], readiness_verdict: 'Error' });
    } finally {
      setCritiqueLoading(false);
    }
  };

  const handleExportNarrative = async () => {
    if (!currentRoadmapId) return;
    setNarrativeLoading(true);
    try {
      const result = await apiService.exportNarrative(currentRoadmapId, i18n.language);
      // Open narrative in new window as formatted HTML
      const html = `<!DOCTYPE html><html><head><title>${result.title}</title><style>body{font-family:Georgia,serif;max-width:800px;margin:40px auto;padding:20px;color:#1e293b;line-height:1.7}h1{color:#1e1b4b;border-bottom:2px solid #6366f1;padding-bottom:12px}h2{color:#312e81;margin-top:32px}p{margin:12px 0}</style></head><body><h1>${result.title}</h1>${result.sections.map(s => `<h2>${s.heading}</h2><p>${s.content.replace(/\n/g, '</p><p>')}</p>`).join('')}</body></html>`;
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {
      alert(t('ai.narrativeFailed'));
    } finally {
      setNarrativeLoading(false);
    }
  };

  const progress = getProgress();
  const steps = roadmapData.steps;

  const stepList = Object.entries(STEP_LABELS).map(([key, labelKey], idx) => ({
    key,
    label: t(labelKey),
    num: idx + 1,
    completed: isStepCompleted(key),
    route: STEP_ROUTES[key]
  }));

  const completedCount = stepList.filter(s => s.completed).length;

  const statItems = [
    { label: t('dashboard.stats.objectives'), value: steps.strategic_objectives?.objectives?.length || 0, color: '#6366f1' },
    { label: t('dashboard.stats.components'),  value: steps.action_components?.components?.length || 0, color: 'var(--status-sky)' },
    { label: t('dashboard.stats.resources'),   value: [
        ...(steps.resources?.human_resources || []),
        ...(steps.resources?.financial_resources || []),
        ...(steps.resources?.technology_resources || []),
        ...(steps.resources?.other_resources || [])
      ].length, color: 'var(--status-green)' },
    { label: t('dashboard.stats.risks'),  value: steps.risks?.risk_items?.length || 0, color: 'var(--status-amber)' },
    { label: t('dashboard.stats.phases'), value: steps.timeline?.phases?.length || 0, color: 'var(--status-sky)' },
    { label: t('dashboard.stats.kpis'),   value: steps.performance?.kpis?.length || 0, color: 'var(--ai-accent)' }
  ];

  const highPriorityObjectives = (steps.strategic_objectives?.objectives || []).filter(o => o.priority === 'High');
  const highRisks = (steps.risks?.risk_items || []).filter(r => r.likelihood === 'High' || r.impact === 'High');
  const hasAttention = highPriorityObjectives.length > 0 || highRisks.length > 0;

  const timelineStart = steps.timeline?.start_date;
  const timelineEnd   = steps.timeline?.end_date;
  const upcomingPhases = (steps.timeline?.phases || []).slice(0, 3);

  return (
    <PageContainer>

      {/* Header */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid var(--line-1)',
          borderRadius: 2,
          p: { xs: 2.5, md: 3.5 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0, top: 0, bottom: 0, width: 3,
            bgcolor: progress === 100 ? '#22c55e' : '#6366f1'
          }
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ pl: 1 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--fg-strong)', mb: 0.25 }}>
              {t('dashboard.title')}
            </Typography>
            {roadmapData.project_name && (
              <Typography variant="body2" sx={{ color: 'var(--accent-fg)', fontWeight: 500 }}>
                {roadmapData.project_name}
              </Typography>
            )}
          </Box>
          <Stack alignItems={{ xs: 'flex-start', sm: 'flex-end' }} spacing={0.75} sx={{ minWidth: 180 }}>
            <Stack direction="row" alignItems="baseline" spacing={0.5}>
              <Typography sx={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--fg-strong)', lineHeight: 1 }}>
                {progress}
              </Typography>
              <Typography sx={{ fontSize: '0.9rem', color: 'var(--fg-muted)', fontWeight: 600 }}>%</Typography>
              <Typography sx={{ fontSize: '0.78rem', color: 'var(--fg-faint)', ml: 0.5 }}>
                · {completedCount}/{stepList.length} {t('common.complete')}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                width: '100%', height: 5, borderRadius: 10,
                bgcolor: 'var(--line-1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 10,
                  background: progress === 100
                    ? 'linear-gradient(90deg, #22c55e, #16a34a)'
                    : 'linear-gradient(90deg, #6366f1, #3b82f6)'
                }
              }}
            />
          </Stack>
        </Stack>
      </Box>

      {/* AI Actions Row */}
      {currentRoadmapId && (
        <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', useFlexGap: true }}>
          <Button
            size="small"
            onClick={handleCritique}
            disabled={critiqueLoading}
            startIcon={critiqueLoading ? <CircularProgress size={14} /> : <AdaptationIcon />}
            sx={{
              border: '1px solid rgba(168,85,247,0.3)',
              color: 'var(--ai-accent)',
              background: 'linear-gradient(135deg, rgba(168,85,247,0.08), rgba(99,102,241,0.08))',
              '&:hover': { borderColor: '#a855f7', background: 'linear-gradient(135deg, rgba(168,85,247,0.18), rgba(99,102,241,0.18))' },
              fontWeight: 600, fontSize: '0.78rem', textTransform: 'none', px: 2
            }}
          >
            {critiqueLoading ? t('ai.analyzingRoadmap') : t('ai.critique')}
          </Button>
          <Button
            size="small"
            onClick={handleExportNarrative}
            disabled={narrativeLoading}
            startIcon={narrativeLoading ? <CircularProgress size={14} /> : <AdaptationIcon />}
            sx={{
              border: '1px solid rgba(99,102,241,0.3)',
              color: 'var(--accent-fg)',
              background: 'rgba(99,102,241,0.08)',
              '&:hover': { borderColor: 'var(--accent-fg)', background: 'rgba(99,102,241,0.18)' },
              fontWeight: 600, fontSize: '0.78rem', textTransform: 'none', px: 2
            }}
          >
            {narrativeLoading ? t('ai.generating') : t('ai.exportNarrative')}
          </Button>
        </Stack>
      )}

      {/* Stats row */}
      <Grid container spacing={1.5}>
        {statItems.map((stat) => (
          <Grid item xs={6} sm={4} md={2} key={stat.label}>
            <Box
              sx={{
                bgcolor: 'background.paper',
                border: '1px solid var(--line-1)',
                borderRadius: 2,
                p: 2,
                textAlign: 'center',
                transition: 'border-color 0.15s ease',
                '&:hover': { borderColor: stat.color + '44' }
              }}
            >
              <Typography sx={{ fontSize: '1.6rem', fontWeight: 800, color: stat.color, lineHeight: 1, mb: 0.5 }}>
                {stat.value}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'var(--fg-faint)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {stat.label}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Steps + Priority */}
      <Grid container spacing={2}>

        {/* Steps completion */}
        <Grid item xs={12} md={6}>
          <Box sx={{ bgcolor: 'background.paper', border: '1px solid var(--line-1)', borderRadius: 2, p: 2.5, height: '100%' }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--fg-faint)', textTransform: 'uppercase', letterSpacing: 1, mb: 2 }}>
              {t('dashboard.stepCompletion')}
            </Typography>
            <Stack spacing={0.25}>
              {stepList.map((step) => (
                <Box
                  key={step.key}
                  onClick={() => navigate(step.route)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    px: 1.5, py: 0.85,
                    borderRadius: 1.5, cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    '&:hover': { bgcolor: 'rgba(99,102,241,0.06)', '& .step-arrow': { opacity: 1 } }
                  }}
                >
                  <Box
                    sx={{
                      width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: step.completed ? 'rgba(34,197,94,0.12)' : 'var(--line-1)',
                      border: '1px solid ' + (step.completed ? 'rgba(34,197,94,0.35)' : 'var(--line-2)'),
                      color: step.completed ? 'var(--status-green)' : 'var(--fg-faint)'
                    }}
                  >
                    {step.completed
                      ? <CheckIcon sx={{ fontSize: 13 }} />
                      : <Typography sx={{ fontSize: '0.62rem', fontWeight: 700 }}>{step.num}</Typography>
                    }
                  </Box>
                  <Box sx={{ color: step.completed ? 'var(--status-green)' : 'var(--fg-faint)', flexShrink: 0 }}>
                    {STEP_ICONS[step.key]}
                  </Box>
                  <Typography sx={{ fontSize: '0.845rem', fontWeight: step.completed ? 500 : 400, color: step.completed ? 'var(--fg-strong)' : 'var(--fg-muted)', flex: 1 }}>
                    {step.label}
                  </Typography>
                  <ArrowIcon className="step-arrow" sx={{ fontSize: 14, color: 'var(--fg-faint)', opacity: 0, transition: 'opacity 0.15s', flexShrink: 0 }} />
                </Box>
              ))}
            </Stack>
          </Box>
        </Grid>

        {/* Priority + Project info */}
        <Grid item xs={12} md={6}>
          <Stack spacing={2} sx={{ height: '100%' }}>
            <Box sx={{ bgcolor: 'background.paper', border: '1px solid var(--line-1)', borderRadius: 2, p: 2.5, flex: 1 }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--fg-faint)', textTransform: 'uppercase', letterSpacing: 1, mb: 2 }}>
                {t('dashboard.highPriorityItems')}
              </Typography>

              {!hasAttention && (
                <Typography sx={{ fontSize: '0.84rem', color: 'var(--fg-faint)', fontStyle: 'italic' }}>
                  {t('dashboard.noHighPriority')}
                </Typography>
              )}

              {highPriorityObjectives.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
                    <ObjectiveIcon sx={{ fontSize: 14, color: 'var(--ai-accent)' }} />
                    <Typography sx={{ fontSize: '0.73rem', fontWeight: 600, color: 'var(--fg-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {t('dashboard.highPriorityObjectives')}
                    </Typography>
                  </Stack>
                  <Stack direction="row" flexWrap="wrap" gap={0.75}>
                    {highPriorityObjectives.map((obj, i) => (
                      <Chip key={i} label={obj.name} size="small"
                        sx={{ height: 24, fontSize: '0.72rem', bgcolor: 'rgba(167,139,250,0.1)', color: 'var(--ai-accent)', border: '1px solid rgba(167,139,250,0.25)', '& .MuiChip-label': { px: 1 } }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {highRisks.length > 0 && (
                <Box>
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
                    <RiskIcon sx={{ fontSize: 14, color: 'var(--status-amber)' }} />
                    <Typography sx={{ fontSize: '0.73rem', fontWeight: 600, color: 'var(--fg-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {t('dashboard.highRisks')}
                    </Typography>
                  </Stack>
                  <Stack spacing={0.75}>
                    {highRisks.slice(0, 4).map((risk, i) => (
                      <Typography key={i} sx={{ fontSize: '0.82rem', color: 'var(--fg-muted)', display: 'flex', gap: 1 }}>
                        <Box component="span" sx={{ color: 'var(--status-amber)', flexShrink: 0 }}>▲</Box>
                        {risk.description}
                      </Typography>
                    ))}
                    {highRisks.length > 4 && (
                      <Typography sx={{ fontSize: '0.75rem', color: 'var(--fg-faint)' }}>+{highRisks.length - 4} more</Typography>
                    )}
                  </Stack>
                </Box>
              )}
            </Box>

            {roadmapData.project_name && (
              <Box sx={{ bgcolor: 'background.paper', border: '1px solid var(--line-1)', borderRadius: 2, p: 2.5 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--fg-faint)', textTransform: 'uppercase', letterSpacing: 1, mb: 1.5 }}>
                  {t('dashboard.projectInfo')}
                </Typography>
                <Stack spacing={1}>
                  {[
                    { label: t('dashboard.projectName'), value: roadmapData.project_name },
                    { label: t('dashboard.type'),        value: roadmapData.project_type },
                    { label: t('dashboard.created'),     value: roadmapData.created_date }
                  ].filter(r => r.value).map(row => (
                    <Stack key={row.label} direction="row" justifyContent="space-between" alignItems="center">
                      <Typography sx={{ fontSize: '0.75rem', color: 'var(--fg-faint)' }}>{row.label}</Typography>
                      <Typography sx={{ fontSize: '0.82rem', color: 'var(--fg-strong)', fontWeight: 500 }}>{row.value}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </Grid>
      </Grid>

      {/* Timeline */}
      {timelineStart && timelineEnd && (
        <Box sx={{ bgcolor: 'background.paper', border: '1px solid var(--line-1)', borderRadius: 2, p: 2.5 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <TimelineIcon sx={{ fontSize: 16, color: 'var(--status-sky)' }} />
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--fg-faint)', textTransform: 'uppercase', letterSpacing: 1 }}>
              {t('dashboard.projectTimeline')}
            </Typography>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'center' }}>
            <Box>
              <Typography sx={{ fontSize: '0.7rem', color: 'var(--fg-faint)', mb: 0.25 }}>{t('dashboard.projectStart')}</Typography>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--fg-strong)' }}>{timelineStart}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.7rem', color: 'var(--fg-faint)', mb: 0.25 }}>{t('dashboard.projectEnd')}</Typography>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--fg-strong)' }}>{timelineEnd}</Typography>
            </Box>
            {upcomingPhases.length > 0 && (
              <>
                <Divider orientation="vertical" flexItem sx={{ borderColor: 'var(--line-1)', display: { xs: 'none', sm: 'block' } }} />
                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                  {upcomingPhases.map((phase, i) => (
                    <Box key={i}>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--fg-strong)' }}>{phase.name}</Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: 'var(--fg-faint)' }}>{phase.start_date} → {phase.end_date}</Typography>
                    </Box>
                  ))}
                </Stack>
              </>
            )}
          </Stack>
        </Box>
      )}

      {/* AI Critique Modal */}
      <AICritiqueModal
        open={critiqueOpen}
        onClose={() => setCritiqueOpen(false)}
        critiqueData={critiqueData}
        loading={critiqueLoading}
      />

    </PageContainer>
  );
}
